import React, { useState } from 'react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const ImportDialog = ({ onClose, onComplete }) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [importing, setImporting] = useState(false);

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    setFile(selectedFile);

    // Get preview
    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      const response = await fetch(`${API_URL}/api/import/preview`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setPreview(data);
      }
    } catch (error) {
      console.error('Failed to preview:', error);
      toast.error('Failed to preview file');
    }
  };

  const handleImport = async () => {
    if (!file) return;

    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${API_URL}/api/import/commit`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Imported ${data.bookmarks_imported} bookmarks in ${data.categories_imported} categories`);
        onComplete();
      } else {
        throw new Error('Import failed');
      }
    } catch (error) {
      console.error('Failed to import:', error);
      toast.error('Failed to import bookmarks');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-card rounded-lg shadow-xl max-w-lg w-full mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-border">
          <h2 className="text-2xl font-bold text-foreground">Import Bookmarks</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Import your bookmarks from Chrome HTML export
          </p>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Select HTML file
            </label>
            <input
              type="file"
              accept=".html"
              onChange={handleFileChange}
              className="w-full px-4 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring bg-background"
              data-testid="import-file-input"
            />
          </div>

          {preview && (
            <div className="p-4 bg-secondary/50 rounded-lg">
              <h3 className="font-medium text-foreground mb-2">Preview</h3>
              <div className="space-y-1 text-sm">
                <p className="text-muted-foreground">
                  <span className="font-medium text-foreground">{preview.categories_found}</span> categories found
                </p>
                <p className="text-muted-foreground">
                  <span className="font-medium text-foreground">{preview.bookmarks_found}</span> bookmarks found
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="p-6 border-t border-border flex gap-3">
          <button
            onClick={handleImport}
            disabled={!file || importing}
            className="flex-1 bg-primary text-primary-foreground py-2.5 rounded-lg font-medium hover:bg-primary/90 disabled:opacity-50"
            data-testid="import-confirm-button"
          >
            {importing ? 'Importing...' : 'Import'}
          </button>
          <button
            onClick={onClose}
            className="flex-1 border border-input py-2.5 rounded-lg font-medium hover:bg-accent/10"
            data-testid="import-cancel-button"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImportDialog;
